/**
 * Date utility functions using date-fns for consistency
 */
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addDays,
  addMonths,
  subMonths,
  getYear,
  getDate,
  isSameYear,
} from 'date-fns';

/**
 * Get the full month name from a 1-indexed month number
 * @param month - Month number (1-12)
 */
export function getMonthName(month: number): string {
  // Create a date in the given month (month is 0-indexed in Date constructor)
  const date = new Date(2000, month - 1, 1);
  return format(date, 'MMMM');
}

/**
 * Get the abbreviated month name from a 1-indexed month number
 * @param month - Month number (1-12)
 */
export function getShortMonthName(month: number): string {
  const date = new Date(2000, month - 1, 1);
  return format(date, 'MMM');
}

/**
 * Format a date, including year only if it's not the current year
 * Uses abbreviated month names with period (Jan., Feb., etc.)
 */
export function formatDateWithConditionalYear(date: Date, currentYear?: number): string {
  const year = currentYear ?? getYear(new Date());
  const month = format(date, 'MMM');
  const day = getDate(date);
  const dateYear = getYear(date);

  if (dateYear !== year) {
    return `${month}. ${day}, ${dateYear}`;
  }
  return `${month}. ${day}`;
}

/**
 * Format a date range for display
 * Handles same-year optimization (omits year if both dates are in current year)
 */
export function formatDateRange(start: Date, end: Date, currentYear?: number): string {
  const year = currentYear ?? getYear(new Date());
  return `${formatDateWithConditionalYear(start, year)} - ${formatDateWithConditionalYear(end, year)}`;
}

/**
 * Get the start of the current week (Monday)
 */
export function getWeekStart(date: Date): Date {
  return startOfWeek(date, { weekStartsOn: 1 }); // Monday
}

/**
 * Get the end of the current week (Sunday)
 */
export function getWeekEnd(date: Date): Date {
  return endOfWeek(date, { weekStartsOn: 1 }); // Sunday
}

/**
 * Get week boundaries for a given date
 */
export function getWeekBounds(date: Date): { start: Date; end: Date } {
  return {
    start: getWeekStart(date),
    end: getWeekEnd(date),
  };
}

/**
 * Get month boundaries for a given date
 */
export function getMonthBounds(date: Date): { start: Date; end: Date } {
  return {
    start: startOfMonth(date),
    end: endOfMonth(date),
  };
}

/**
 * Get date range for "tomorrow"
 */
export function getTomorrow(from: Date = new Date()): Date {
  return addDays(from, 1);
}

/**
 * Get date range for "next week"
 */
export function getNextWeekBounds(from: Date = new Date()): { start: Date; end: Date } {
  const nextWeek = addDays(from, 7);
  return getWeekBounds(nextWeek);
}

/**
 * Get date range for "next month"
 */
export function getNextMonthBounds(from: Date = new Date()): { start: Date; end: Date } {
  const nextMonth = addMonths(from, 1);
  return getMonthBounds(nextMonth);
}

/**
 * Get date range for "last month"
 */
export function getLastMonthBounds(from: Date = new Date()): { start: Date; end: Date } {
  const lastMonth = subMonths(from, 1);
  return getMonthBounds(lastMonth);
}

/**
 * Format a date as ISO date string (YYYY-MM-DD)
 */
export function toISODateString(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/**
 * Format a date for display with weekday
 * e.g., "Monday, Jan. 15" or "Monday, Jan. 15, 2024" if different year
 */
export function formatDateWithWeekday(date: Date, currentYear?: number): string {
  const year = currentYear ?? getYear(new Date());
  const weekday = format(date, 'EEEE');
  const month = format(date, 'MMM');
  const day = getDate(date);
  const dateYear = getYear(date);

  if (dateYear !== year) {
    return `${weekday}, ${month}. ${day}, ${dateYear}`;
  }
  return `${weekday}, ${month}. ${day}`;
}

/**
 * Format duration in minutes to human-readable format
 * e.g., 90 -> "1h 30m", 45 -> "45m"
 */
export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
}

/**
 * Get timezone abbreviation from a date and IANA timezone
 * e.g., "America/New_York" -> "EST" or "EDT"
 */
function getTimezoneAbbr(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'short',
  }).formatToParts(date);

  const tzPart = parts.find(p => p.type === 'timeZoneName');
  return tzPart?.value || timeZone;
}

/**
 * Format a date/time showing client timezone with abbreviation and user's local time
 * Format: "Jan 12, 8:32 AM MST (10:32 AM)"
 *
 * @param date - Date string or Date object (in UTC/ISO format)
 * @param clientTimeZone - Client's IANA timezone (e.g., "America/New_York")
 * @returns Formatted string with client TZ and local time
 */
export function formatDateTimeWithClientTZ(
  date: Date | string,
  clientTimeZone: string | null
): string {
  const d = typeof date === 'string' ? new Date(date) : date;

  // Default to Pacific if no timezone provided
  const tz = clientTimeZone || 'America/Los_Angeles';

  // Format in client's timezone
  const clientTime = d.toLocaleString('en-US', {
    timeZone: tz,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  const tzAbbr = getTimezoneAbbr(d, tz);

  // Format user's local time (just time, no date since it's contextual)
  const localTime = d.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  // Check if local time is different from client time
  const clientTimeOnly = d.toLocaleString('en-US', {
    timeZone: tz,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  // Only show local time if different from client time
  if (clientTimeOnly === localTime) {
    return `${clientTime} ${tzAbbr}`;
  }

  return `${clientTime} ${tzAbbr} (${localTime})`;
}
